import { Injectable } from "@angular/core";
import { AIServices } from "../ai/ai-services";
import { checkEpmChange, EpmDecisionEvidence, EpmFutureStateDecision, EPM_FUTURE_STATE_LIMITS } from "../common/epm-decisions";
import { CURRENT_STATE_PROJECT_KEY, GraphData, ORGANISATION_MODEL_KEY } from "../common/linqset-model";
import { createProject, getUnsortedModelList, Organisation, Project, PROJECT_DESCRIPTION_MAX, PROJECT_NAME_MAX } from "../common/organisation";
import { MODEL_API, ORGANISATION_API, RepositoryService } from "../linq-utils/repository.service";
import { OrganisationService } from "../organisation/organisation.service";
import { EpmAppliedChange, graphForWorkspace, modelsForChanges, PlannedChange, planEpmFutureState } from "./epm-future-state";

const COMPONENT = "EPM future state"

export type EpmFutureStateStep = 'planning' | 'creating' | 'applying' | 'scoring'

// a future state generated from decisions: a new project, which the user names or deletes once they've looked at it
export interface EpmFutureState {
    project:Project
    decisions:string[]
    changes:EpmAppliedChange[]
}

@Injectable({ providedIn: 'root' })
export class EpmFutureStateService {
    constructor(private aiServices:AIServices, private organisationService:OrganisationService, private repoService:RepositoryService) {}

    // asks AI for the changes that carry out the decisions (the overview's recommendations as decisions), and makes them
    // in a new project, which the user can rename or delete. If it fails part way, the project is removed
    async generate(organisation:Organisation, evidence:EpmDecisionEvidence, decisions:EpmFutureStateDecision[], progress:(step:EpmFutureStateStep) => void, base?:Project):Promise<EpmFutureState> {
        progress('planning')
        const { chosen, usable, rejected } = await this.propose(organisation, evidence, decisions)

        progress('creating')
        const project = await this.createProject(organisation, futureStateName(chosen), `Future state for: ${chosen.map(d => d.title).join('; ')}`, base)

        try {
            progress('applying')
            // the project's models: current state's, or for one that copies a project, that project's as copied
            const models = getUnsortedModelList(organisation, project)
            const modelKeys = modelsForChanges(usable, models)
            const [organisationModel, ...graphs] = await Promise.all([ORGANISATION_MODEL_KEY, ...modelKeys].map(key =>
                this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(organisation._key, key, project._key)), COMPONENT, "Load the project's model")))

            const plan = planEpmFutureState(usable, {
                organisationKey: organisation._key,
                projectKey: project._key,
                models,
                organisationModel,
                graphs: Object.fromEntries(modelKeys.map((key, i) => [key, graphForWorkspace(graphs[i], organisation, project)])),
            })
            if (!plan.applied.some(c => c.applied)) throw new Error("None of AI's changes matched the model")

            // roles and systems first, as the models' changes can use the ones it adds
            const order = Object.keys(plan.changes).sort((a, b) => (a === ORGANISATION_MODEL_KEY ? -1 : b === ORGANISATION_MODEL_KEY ? 1 : 0))
            for (const key of order) {
                await this.repoService.callSingleAPI(this.repoService.update(MODEL_API(organisation._key, key, project._key), plan.changes[key]), COMPONENT, "Save the future state")
            }

            progress('scoring')
            await this.repoService.callSingleAPI(this.repoService.update(`${ORGANISATION_API(organisation._key, project._key)}/refresh`, {}), COMPONENT, "Score the future state")
            // each model's insights for the new project were worked out on the server, and the scorecard totals them
            // (such as the value produced), so the models are brought up to date
            await this.refreshModels(organisation)

            return { project, decisions: chosen.map(d => d.title), changes: [...plan.applied, ...rejected] }
        }catch(err) {
            await this.discard(project).catch(e => console.error("Unable to remove the unfinished future state", e))
            throw err
        }
    }

    // AI's changes for the decisions, without making them: those LINQ can make, and those naming nodes the model
    // doesn't have, which are reported rather than made
    async propose(organisation:Organisation, evidence:EpmDecisionEvidence, decisions:EpmFutureStateDecision[]):Promise<{ chosen:EpmFutureStateDecision[], usable:PlannedChange[], rejected:EpmAppliedChange[] }> {
        const chosen = decisions.slice(0, EPM_FUTURE_STATE_LIMITS.decisions)
        const proposed = await this.aiServices.generateEpmFutureState(organisation._key, CURRENT_STATE_PROJECT_KEY, {
            evidence,
            decisions: chosen.map(d => ({ title: d.title, lever: d.lever, signal: d.signal, rationale: d.rationale, nodes: d.nodes })),
        })
        const rejected:EpmAppliedChange[] = []
        const usable = proposed.slice(0, EPM_FUTURE_STATE_LIMITS.changes).filter(c => {
            const reason = checkEpmChange(c, evidence)
            if (reason) rejected.push({ decision: c.decision, description: c.description, applied: false, reason })
            return !reason
        })
        if (usable.length === 0) throw new Error("AI couldn't turn these decisions into changes LINQ can make to the model")
        return { chosen, usable, rejected }
    }

    // a new project for a future state. Given a project, it's made as a copy of it, so the future state builds on that
    // project rather than on current state; the organisation's models then include the copies of those made in it
    async createProject(organisation:Organisation, name:string, description:string, base?:Project):Promise<Project> {
        const project = createProject()
        project.name = name.slice(0, PROJECT_NAME_MAX)
        project.description = (base ? `${description} (based on ${base.name})` : description).slice(0, PROJECT_DESCRIPTION_MAX)
        if (!base) {
            await this.organisationService.createProject(project)
            return project
        }
        await this.organisationService.copyProject(base, project)
        const latest = await this.organisationService.getOrganisation(organisation._key)
        if (latest?.models) organisation.models = latest.models
        return project
    }

    // the organisation's models as the server has them, with the insights it worked out after a change
    async refreshModels(organisation:Organisation) {
        const latest = await this.organisationService.getOrganisation(organisation._key)
        if (latest?.models) organisation.models = latest.models
    }

    async discard(project:Project) {
        await this.organisationService.deleteProject(project)
    }

    // names the future state's project as the user chooses
    async rename(project:Project, name:string) {
        await this.organisationService.updateProject({ ...project, name })
    }
}

// a name for the project, from the decisions it carries out, until the user gives it their own
export const futureStateName = (decisions:{ title:string }[]) => {
    const first = decisions[0]?.title || 'decisions'
    const rest = decisions.length > 1 ? ` +${decisions.length - 1}` : ''
    const name = `Future state: ${first}`
    return name.length + rest.length > PROJECT_NAME_MAX ? name.slice(0, PROJECT_NAME_MAX - rest.length - 1).trimEnd() + '…' + rest : name + rest
}
